// Packages
import { useState, useMemo } from "react";

// Hooks
import useDialogForm, {
  type DialogFormData,
} from "@common/hooks/useDialogForm";
import { useGroupHooks } from "@common/hooks/useGroupHooks";
import { useBanner } from "@common/hooks/useBanner";
import { useWorkspace } from "@workspace/hooks/useReduxWorkspace";
import { errorMessage } from "@jkast90/kast-common/format";
import type { NormalizedGroup } from "@common/types/entities";

// Components
import { Switch, TextField } from "@jkast90/kast-common/ui";
import { BaseDialog, DialogActions } from "@common/components/ui";

// Styling
import dialogCss from "@common/styles/Dialog.module.css";
import { logger } from "@common/utils/logger";

interface GroupDialogProps {
  open: boolean;
  onClose: () => void;
  initialName?: string;
  /** "personal" or "team". */
  initialView?: string;
  /** "links" or "notes". */
  initialType?: string;
  groupId?: number | null;
  workspaceId?: number | null;
  /**
   * Called after the dialog has saved the group (it saves it itself), with
   * the group as the API returned it.
   */
  onSaved?: (group: NormalizedGroup, outcome: { created: boolean }) => void;
}

export default function GroupDialog({
  open,
  onClose,
  initialName = "",
  initialView = "team", // ← "personal" or "team"
  initialType = "links",
  groupId = null,
  workspaceId = null,
  onSaved,
}: GroupDialogProps) {
  const { showBanner } = useBanner();
  const { loadAllWorkspaces } = useWorkspace();
  const { createGroup, updateGroup } = useGroupHooks();

  const [type, setType] = useState(initialType);
  const [view, setView] = useState(initialView);

  // Memoize initial values to prevent unnecessary resets when other state changes
  const initialValues = useMemo(
    () => ({
      groupName: initialName,
    }),
    [initialName],
  );

  const { isSubmitting, getFieldRef, handleSubmit } = useDialogForm(
    open,
    initialValues,
  );

  // Follow the props when they change (adjusting state during render, not
  // in an effect, so there is no extra render with the stale value).
  const [prevInitialView, setPrevInitialView] = useState(initialView);
  if (initialView !== prevInitialView) {
    setPrevInitialView(initialView);
    setView(initialView);
  }
  const [prevInitialType, setPrevInitialType] = useState(initialType);
  if (initialType !== prevInitialType) {
    setPrevInitialType(initialType);
    setType(initialType);
  }

  const onFormSubmit = async (formData: DialogFormData) => {
    const groupName = formData.groupName?.trim();
    if (!groupName) return;

    const groupData = {
      group_name: groupName,
      group_type: type,
      group_expanded: true,
      view, // include personal/team
      ...(workspaceId && { workspace_id: workspaceId }),
    };

    logger.debug("🔍 [GROUP_TYPE_DEBUG] Frontend sending payload:", {
      group_name: groupData.group_name,
      group_type: groupData.group_type,
      group_expanded: groupData.group_expanded,
      view: groupData.view,
      workspace_id: groupData.workspace_id,
    });

    try {
      let savedGroup: NormalizedGroup;
      if (groupId) {
        // EDIT
        savedGroup = await updateGroup(groupId, groupData);
      } else {
        // CREATE
        const newGroup = await createGroup(groupData);
        savedGroup = newGroup;
        logger.debug("🔍 [GROUP_TYPE_DEBUG] Frontend received response:", {
          group_id: newGroup?.group_id,
          group_name: newGroup?.group_name,
          group_type: newGroup?.group_type,
          view: newGroup?.view,
        });
      }
      // Reload workspaces (groups derive from them via cross-slice reducer)
      await loadAllWorkspaces({ forceRefresh: true });
      onClose();
      onSaved?.(savedGroup, { created: !groupId });
    } catch (err) {
      showBanner(`Failed to save group: ${errorMessage(err)}`);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={initialName ? "Edit Group" : "Add Group"}
      data-testid="group-dialog"
      footer={
        <DialogActions
          form="group-form"
          onCancel={onClose}
          onSubmit={() => {}} // Form submission handled by the form's onSubmit
          isLoading={isSubmitting}
          data-testid="save-group-btn"
          needsConnection
        />
      }
    >
      <form
        id="group-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onFormSubmit);
        }}
        className={`kui-form ${dialogCss.form}`}
      >
        <TextField
          label="Group Name"
          ref={(input) => {
            getFieldRef("groupName").current = input;
          }}
          required
          id="group-name-input"
        />

        <Switch
          id="group-type-toggle"
          label="Notes group"
          description="Off, the group holds links."
          checked={type === "notes"}
          onChange={(on) => setType(on ? "notes" : "links")}
        />

        <Switch
          id="group-view-toggle"
          label="Team group"
          description="Off, the group is personal."
          checked={view === "team"}
          onChange={(on) => setView(on ? "team" : "personal")}
        />
      </form>
    </BaseDialog>
  );
}
